import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getAdminSettings, upsertAdminSettings } from "@/integrations/backend/admin-settings";
import { useToast } from "@/hooks/use-toast";
import { Save } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NotificationsView } from "./NotificationsView";
import LegalView from "./LegalView";
import FAQView from "./FAQView";
import PricingTiersView from "./PricingTiersView";
import PromptsAdminView from "./PromptsAdminView";
import ChecklistView from "./ChecklistView";
import AssessmentsView from "./AssessmentsView";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";

const DEFAULT_SENDER_DOMAIN = "healthq.com";

const settingsQueryKey = queryKeys.admin.settings;

export function SettingsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [senderName, setSenderName] = useState("");
  const [senderEmailLocalPart, setSenderEmailLocalPart] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: settingsQueryKey,
    queryFn: getAdminSettings,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...settingsQueryKey]),
  });

  const saveMutation = useMutation({
    mutationFn: upsertAdminSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKey });
    },
  });

  useEffect(() => {
    const data = settingsQuery.data;
    if (data) {
      setSenderName(data.default_sender_name);
      const email = data.default_sender_email || "";
      const atIndex = email.indexOf("@");
      const localPart = atIndex === -1 ? email : email.slice(0, atIndex);
      setSenderEmailLocalPart(localPart);
    } else if (!settingsQuery.isLoading && data === null) {
      setSenderName("");
      setSenderEmailLocalPart("");
    }
  }, [settingsQuery.data, settingsQuery.isLoading]);

  // Initialize activeSettingsTab from URL, default to "general"
  const getInitialSettingsTab = () => {
    const urlTab = searchParams.get("settingsTab");
    const validTabs = ["general", "notifications", "legal", "faqs", "pricing", "prompts", "checklist", "assessments"];
    if (urlTab && validTabs.includes(urlTab)) {
      return urlTab;
    }
    return "general";
  };

  const [activeSettingsTab, setActiveSettingsTab] = useState<string>(getInitialSettingsTab);

  // Sync activeSettingsTab with URL
  useEffect(() => {
    const urlTab = searchParams.get("settingsTab");
    if (urlTab !== activeSettingsTab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("settingsTab", activeSettingsTab);
      setSearchParams(newParams, { replace: true });
    }
  }, [activeSettingsTab, searchParams, setSearchParams]);

  // Read from URL when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlTab = searchParams.get("settingsTab");
    const validTabs = ["general", "notifications", "legal", "faqs", "pricing", "prompts", "checklist", "assessments"];
    if (urlTab && validTabs.includes(urlTab)) {
      setActiveSettingsTab((prev) => (prev !== urlTab ? urlTab : prev));
    }
  }, [searchParams]);

  const handleSave = async () => {
    if (!senderName.trim() || !senderEmailLocalPart.trim()) {
      toast({
        title: "Required fields",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    const fullSenderEmail = `${senderEmailLocalPart.trim()}@${DEFAULT_SENDER_DOMAIN}`;

    setSaving(true);
    try {
      await saveMutation.mutateAsync({
        default_sender_name: senderName,
        default_sender_email: fullSenderEmail,
      });

      toast({
        title: "Success",
        description: "Settings saved successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (settingsQuery.isLoading) {
    return <div className="flex items-center justify-center p-8">Loading...</div>;
  }

  if (settingsQuery.isError) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        {(settingsQuery.error as Error)?.message ?? "Failed to load settings"}
      </div>
    );
  }

  return (
    <Tabs value={activeSettingsTab} onValueChange={setActiveSettingsTab} className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-600">Configure your application and marketing content.</p>
      </div>
      <TabsList>
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="notifications">Notifications</TabsTrigger>
        <TabsTrigger value="legal">Legal</TabsTrigger>
        <TabsTrigger value="faqs">FAQs</TabsTrigger>
        <TabsTrigger value="pricing">Pricing Tiers</TabsTrigger>
        <TabsTrigger value="prompts">Prompts</TabsTrigger>
        <TabsTrigger value="checklist">Checklist</TabsTrigger>
        <TabsTrigger value="assessments">Assessments</TabsTrigger>
      </TabsList>

      <TabsContent value="general">
        <Card>
          <CardHeader>
            <CardTitle>Email Notifications</CardTitle>
            <CardDescription>Set default sender information for email notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sender-name" className="text-black">
                Default Sender Name
              </Label>
              <Input id="sender-name" placeholder="e.g., FitnessPro Team" value={senderName} onChange={(e) => setSenderName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender-email" className="text-black">
                Default Sender Email
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sender-email"
                  type="text"
                  placeholder="e.g., noreply"
                  value={senderEmailLocalPart}
                  onChange={(e) => setSenderEmailLocalPart(e.target.value)}
                  className="flex-1"
                />
                <span className="text-sm text-gray-600">@{DEFAULT_SENDER_DOMAIN}</span>
              </div>
              <p className="text-xs text-gray-500">You can change the part before the @. The domain is fixed.</p>
            </div>
            <Button onClick={handleSave} disabled={saving || saveMutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {saving || saveMutation.isPending ? "Saving..." : "Save Settings"}
            </Button>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="notifications"><NotificationsView /></TabsContent>
      <TabsContent value="legal"><LegalView /></TabsContent>
      <TabsContent value="faqs"><FAQView /></TabsContent>
      <TabsContent value="pricing"><PricingTiersView /></TabsContent>
      <TabsContent value="prompts"><PromptsAdminView /></TabsContent>
      <TabsContent value="checklist"><ChecklistView /></TabsContent>
      <TabsContent value="assessments"><AssessmentsView /></TabsContent>
    </Tabs>
  );
}
